// Google Analytics 4 (gtag.js) integration for Made For Me Studio.
// Loads gtag asynchronously exactly once, drives manual SPA page_view tracking,
// and exposes typed helpers for the ecommerce funnel + studio-specific events.
// PRIVACY: never pass names, emails, phone numbers, addresses, photo names or any
// PII to these helpers — only anonymous operational attributes.

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-HLNT0X0ZCJ";
const CURRENCY = "USD";

let initialized = false;

export function initAnalytics() {
  if (initialized || typeof window === "undefined" || !GA_MEASUREMENT_ID) return;
  // Guard against loading the tag twice.
  if (!document.getElementById("ga4-gtag-src")) {
    const s = document.createElement("script");
    s.id = "ga4-gtag-src";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
  }
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  window.gtag("js", new Date());
  // send_page_view:false — we emit page_view manually on every SPA route change.
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  initialized = true;
}

function gtagEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (!initialized) initAnalytics();
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

export function trackPageView(path) {
  if (!initialized) initAnalytics();
  gtagEvent("page_view", {
    page_path: path,
    page_location: window.location.href, // preserves utm_* for GA4 attribution
    page_title: document.title,
  });
}

// ---------- Item mapping (anonymous fields only) ----------
export function toItem(p, extra = {}) {
  if (!p) return null;
  const price = extra.price ?? p.unit_price ?? p.starting_price ?? p.price ?? p.variant?.price ?? 0;
  return {
    item_id: p.slug || p.product_slug || p.id || p.product_id,
    item_name: p.name || p.product_name,
    item_category: p.category || extra.item_category,
    price: Number(price) || 0,
    quantity: extra.quantity ?? p.quantity ?? 1,
  };
}

// ---------- Standard GA4 ecommerce ----------
export function trackViewItem(product) {
  const item = toItem(product);
  if (!item) return;
  gtagEvent("view_item", { currency: CURRENCY, value: item.price, items: [item] });
}

export function trackViewItemList(listName, products = []) {
  const items = (products || []).map((p, i) => ({ ...toItem(p), index: i })).filter((x) => x.item_id);
  gtagEvent("view_item_list", { item_list_name: listName, items });
}

export function trackSelectItem(product, listName) {
  const item = toItem(product);
  if (!item) return;
  gtagEvent("select_item", { item_list_name: listName, items: [item] });
}

export function trackBeginCheckout({ items = [], value = 0 } = {}) {
  gtagEvent("begin_checkout", {
    currency: CURRENCY,
    value: Number(value) || 0,
    items: (items || []).map((p) => toItem(p)).filter(Boolean),
  });
}

// Purchase — idempotent per order id (guards against success-page refresh).
export function trackPurchase(order) {
  if (!order) return;
  const id = order.order_number || order.id;
  if (!id) return;
  const key = `ga_purchase_${id}`;
  try { if (localStorage.getItem(key)) return; } catch { /* ignore */ }
  const items = (order.items || []).map((li) => toItem(li)).filter(Boolean);
  gtagEvent("purchase", {
    transaction_id: id,
    value: Number(order.total ?? order.value ?? 0) || 0,
    tax: order.tax != null ? Number(order.tax) : undefined,
    shipping: order.shipping != null ? Number(order.shipping) : undefined,
    currency: CURRENCY,
    items,
  });
  try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
}

export function trackRefund({ transaction_id, value } = {}) {
  gtagEvent("refund", {
    transaction_id,
    value: value != null ? Number(value) : undefined,
    currency: CURRENCY,
  });
}

// ---------- Studio-specific custom events (anonymous attributes only) ----------
export const track = {
  freePreviewStarted: () => gtagEvent("free_preview_started"),
  freePreviewCompleted: () => gtagEvent("free_preview_completed"),
  freePreviewFailed: () => gtagEvent("free_preview_failed"),
  photoUploadStarted: (p = {}) => gtagEvent("photo_upload_started", p),
  photoUploadCompleted: (p = {}) => gtagEvent("photo_upload_completed", p),
  personalizationStarted: (p = {}) => gtagEvent("personalization_started", p),
  personalizationCompleted: (p = {}) => gtagEvent("personalization_completed", p),
  digitalDownloadAvailable: (p = {}) => gtagEvent("digital_download_available", p),
  digitalDownloaded: (p = {}) => gtagEvent("digital_downloaded", p),
  orderTrackingViewed: (p = {}) => gtagEvent("order_tracking_viewed", p),
  faithCollectionViewed: () => gtagEvent("faith_collection_viewed"),
  aiBookGenerationStarted: (p = {}) => gtagEvent("ai_book_generation_started", p),
  aiBookGenerationCompleted: (p = {}) => gtagEvent("ai_book_generation_completed", p),
  aiBookGenerationFailed: (p = {}) => gtagEvent("ai_book_generation_failed", p),
  qualityReviewRequired: (p = {}) => gtagEvent("quality_review_required", p),
  bookApproved: (p = {}) => gtagEvent("book_approved", p),
  printOrderSubmitted: (p = {}) => gtagEvent("print_order_submitted", p),
  printOrderShipped: (p = {}) => gtagEvent("print_order_shipped", p),
};
